<template>
  <div
    :date-name="column.property"
    :class="['table-cell', $attrs.class]"
    :style="$attrs.style"
  >
    <div
      :key="`${rowData.id}-${column.property}`"
      class="table-cell--inner-wrap"
      :class="`gp_autotest_listview_item_cell_${column.property}`"
    >
      <div
        v-if="!$options.methods.isCellExist(rowData, column)"
        class="all-cell"
        @click="$options.methods.notExistCellWarning"
      />
      <template v-else>
        <component
          v-if="column.editor === 'checkbox-selectmode'"
          :is="components.checkboxCell"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :item="rowData"
          :property="column.property"
          :value="isSelectedRow"
          :theme="'blue'"
          @onChange="$emit('select:row')"
        />

        <div
          v-if="column.editor === 'icon'"
          :class="['all-cell', 'flex-center']"
        >
          <component
            :is="components.vueIcon"
            :class="{
              'show-only-row-hover': !rowData[column.property] && !selectMode,
              'blue-icon': rowData[column.property] ? !!rowData[column.property].unread : false,
            }"
            :name="column.iconName"
            :style="{opacity: discolor, display: rowData[column.property] ? 'flex' : 'none'}"
            @click.stop="() => $emit('show:itemSettings', column.property)"
          />
        </div>

        <component
          v-if="column.editor === 'task'"
          :is="components.taskCell"
          :item="rowData"
          :property="column.property"
          :value="rowData[column.property]"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :crossout="crossout"
          :overdue="overdue"
          :can-show-settings="canShowSettings && !selectMode"
          :discolor="discolor"
          @show:settings="$emit('show:itemSettings')"
          @show:task-name-editor="$emit('show:task-name-editor')"
          @onChange="$emit('onChange', $event)"
          @onDelete="$emit('onDelete')"
        />

        <component
          v-if="column.editor === 'project'"
          :is="components.projectCell"
          :item="rowData"
          :property="column.property"
          :property-extra="column.propertyExtra"
          :extra="$options.methods.isShowExtra(extra, column.extra.name)"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :crossout="crossout"
          :style="{opacity: discolor}"
        />

        <component
          v-if="column.editor === 'selectList'"
          :is="components.defaultSelectListCell"
          :item="rowData"
          :column="column"
          :property="column.property"
          :list-type="column.listType"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :discolor="discolor"
          :crossout="crossout"
          @onChange="$emit('onChange', $event)"
        />

        <component
          v-if="column.editor === 'datepicker'"
          :is="components.calendarCell"
          :item="rowData"
          :calendar-config="calendarConfig"
          :property="column.property"
          :value="rowData[column.property]"
          :extra="$options.methods.isShowExtra(extra, column.extra && column.extra.name)"
          :disabled="column.disabled
            || (column.property === 'end_date' && rowData.type === 'milestone')
            || $options.methods.isDateCellBlockedByDependency(rowData, column)"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)
            && !$options.methods.isDateCellBlockedByDependency(rowData, column)"
          :crossout="crossout"
          :overdue="overdue"
          :discolor="discolor"
          @onChange="$emit('onChange', $event)"
        />

        <component
          v-if="column.editor === 'timelog'"
          :is="components.timeLogCell"
          :item="rowData"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :property="column.property"
          :discolor="discolor"
          :crossout="crossout"
        />

        <component
          v-if="column.editor === 'resources'"
          :is="components.resourceCell"
          :item="rowData"
          :property="column.property"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :discolor="discolor"
          :crossout="crossout"
        />

        <component
          v-if="!column.editor"
          :is="components.simpleCell"
          :value="String(rowData[column.property])"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :align="column.align"
          :style="{opacity: discolor}"
          :crossout="crossout"
        />

        <component
          v-if="column.editor === 'text'"
          :is="components.textCell"
          :item="rowData"
          :property="column.property"
          :value="String(rowData[column.property])"
          :type="column.type"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :style="{opacity: discolor}"
          :crossout="crossout"
          @onChange="$emit('onChange', $event)"
        />

        <component
          v-if="column.editor === 'number'"
          :is="components.numberCell"
          :item="rowData"
          :property="column.property"
          :value="rowData[column.property]"
          :type="column.type"
          :disabled="column.property !=='progress' && (column.disabled)"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :discolor="Boolean(discolor)"
          :crossout="crossout"
          @onChange="$emit('onChange', $event)"
        />

        <!-- custom columns -->
        <component
          v-if="column.editor === 'datepicker:custom'"
          :is="components.calendarCell"
          :item="rowData"
          :calendar-config="calendarConfig"
          :for-custom="true"
          :property="column.property"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :value="rowData[column.property]"
          :discolor="discolor"
          :crossout="crossout"
          @onChange="$emit('onChange:custom', $event)"
        />

        <component
          v-if="column.editor === 'text:custom'"
          :is="components.textCell"
          :item="rowData"
          :property="column.property"
          :value="String(rowData[column.property])"
          :type="column.type"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :style="{opacity: discolor}"
          :crossout="crossout"
          @onChange="$emit('onChange:custom', $event)"
        />

        <component
          v-if="column.editor === 'number:custom'"
          :is="components.numberCell"
          :item="rowData"
          :property="column.property"
          :value="rowData[column.property]"
          :type="column.type"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :discolor="Boolean(discolor)"
          :crossout="crossout"
          @onChange="$emit('onChange:custom', $event)"
        />

        <component
          v-if="column.editor === 'checkbox'"
          :is="components.checkboxCell"
          :item="rowData"
          :property="column.property"
          :value="rowData[column.property]"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :style="{opacity: discolor}"
          :crossout="crossout"
          @onChange="$emit('onChange:custom', $event)"
        />

        <component
          v-if="column.editor === 'select'"
          :is="components.selectCell"
          :item="rowData"
          :property="column.property"
          :options="options"
          :template="column.template"
          :value="rowData[column.property]"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :crossout="crossout"
          :discolor="discolor"
          @onChange="$emit('onChange:custom', $event)"
        />

        <component
          v-if="column.editor === 'multiselect'"
          :is="components.multySelectCell"
          :item="rowData"
          :property="column.property"
          :options="options"
          :template="column.template"
          :value="rowData[column.property]"
          :disabled="column.disabled"
          :editable="$options.methods.isEditableCell(rowData.gantt_id, column)"
          :crossout="crossout"
          :discolor="discolor"
          @onChange="$emit('onChange:custom', $event)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import taskCell from './taskCell.vue';
import projectCell from './projectCell.vue';
import calendarCell from './calendarCell.vue';
import defaultSelectListCell from './selectCell/defaultSelectListCell.vue';
import textCell from './textCell.vue';
import numberCell from './numberCell.vue';
import selectCell from './selectCell/selectCell.vue';
import multySelectCell from './selectCell/multySelectCell.vue';
import checkboxCell from './checkboxCell.vue';
import timeLogCell from './timeLogCell.vue';
import resourceCell from './resourceCell.vue';
import simpleCell from './simpleCell.vue';
import vueIcon from '../icons/icon.vue';
import rightsHelper from '../../rightsHelper';
import globalToast from '$$vueCmp/toast';
import TaskSettingsController from '../../../taskSettings/controllers';

export default {
  name: 'Cell',
  inject: {
    components: {
      from: 'components',
      default: {
        taskCell,
        projectCell,
        defaultSelectListCell,
        calendarCell,
        textCell,
        numberCell,
        selectCell,
        multySelectCell,
        checkboxCell,
        timeLogCell,
        resourceCell,
        simpleCell,
        vueIcon,
      },
    },
  },
  props: {
    extra: { type: Array, required: true },
    rowData: { type: Object, required: true },
    column: { type: Object, required: true },
    options: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    discolor: { type: Number, required: false, default: 1 },
    crossout: { type: Boolean, required: false, default: false },
    overdue: { type: Boolean, required: false, default: false },
    canShowSettings: { type: Boolean, required: false, default: false },
    isSelectedRow: { type: Boolean, required: false, default: false },
    selectMode: { type: Boolean, required: false, default: false },
    calendarConfig: { type: Object, required: true },
  },
  emits: [
    'select:row',
    'show:itemSettings',
    'show:task-name-editor',
    'onChange',
    'onDelete',
    'onChange:custom',
  ],
  methods: {
    isCellExist(rowData, column) {
      if (!column.custom) return true;

      const isColumnInProject = column.ganttIds.find(id => id === rowData.gantt_id);

      return column.custom && isColumnInProject;
    },
    isShowExtra(extra, extra_name) {
      return extra_name && extra.includes(extra_name);
    },
    isEditableCell(ganttId, column) {
      return rightsHelper.isEditableCell(ganttId, column);
    },
    isDateCellBlockedByDependency(rowData, column) {
      return TaskSettingsController.checkByLinkDependency(rowData, column.property);
    },
    notExistCellWarning() {
      globalToast.warning(__('msg_cant_edit_column'), { timeout: 4000 });
    },
    cellClick(e) {
      e.stopPropagation();
    },
  },
};
</script>

<style scoped lang="less">
.all-cell{
  height: 100%;
  width: 100%;
}
.flex-center{
  display: flex;
  align-items: center;
  justify-content: center;
}
.table-cell--inner-wrap{
    height: 100%;
    width: 100%;
    overflow: hidden;
    display: flex;
    align-items: center;
  }
.blue-icon{
  color: #1565C0;
}
.discolor{
  opacity: 0.5;
}
</style>
